import { useState } from 'react';
import { DocumentScannerOutlined } from '@mui/icons-material';
import { Box, FormControlLabel, Link, Skeleton, Switch, Typography } from '@mui/material';
import { useGo, useResourceParams, useUpdate } from '@refinedev/core';
import { Edit } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { FieldValues, FormProvider, SubmitHandler } from 'react-hook-form';

import { TextFieldInput, Dropzone } from '@attackiq/components';
import { ATTACKIQ_ID } from '@attackiq/constants';
import { fileToBase64 } from '@attackiq/utils';

import { ShowInAppFormGroup } from '../../components';
import useCurrentResource from '../../hooks/useCurrentResource';
import { ThreatInformedDefense } from '../../types';

import { ActiveInput, HighlightInput } from './create';

const ThreatInformedDefenseEdit = () => {
  const {
    query: { isLoading }
  } = useCurrentResource<ThreatInformedDefense>();
  const [urlEnabled, setUrlEnabled] = useState(false);

  const [selectedFile, setSelectedFile] = useState<File | undefined | null>(null);
  const {
    mutateAsync: editMutateAsync,
    mutation: { isPending: isUpdating }
  } = useUpdate();
  const go = useGo();

  const { id } = useResourceParams();
  const { saveButtonProps, ...formMethods } = useForm<ThreatInformedDefense>({
    refineCoreProps: {
      action: 'edit'
    },
    defaultValues: {
      title: '',
      description: '',
      url: '',
      date: '',
      cover_image: null,
      highlight: false,
      active: false,
      company: ATTACKIQ_ID,
      id: '',
      show_in_enterprise: false,
      show_in_ready: false,
      show_in_flex: false
    }
  });

  const { handleSubmit, setValue } = formMethods;

  const onSubmit: SubmitHandler<ThreatInformedDefense> = async tidContent => {
    if (!id) throw new Error('No ID provided');

    const payload = {
      ...tidContent,
      id
    };

    if (selectedFile && !urlEnabled) payload.cover_image = await fileToBase64(selectedFile);

    const { data: editedTid } = await editMutateAsync({
      id,
      resource: 'threat_informed_defense',
      values: payload
    });

    if (!editedTid.id) return;

    go({
      to: {
        resource: 'threat_informed_defense',
        action: 'show',
        id: editedTid.id
      }
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form name="edit-module-form" onSubmit={handleSubmit(onSubmit as SubmitHandler<FieldValues>)} autoComplete="off">
        <Edit isLoading={isUpdating} recordItemId={id} saveButtonProps={{ type: 'submit' }} canDelete={false}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={500} width="100%" />
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              <ActiveInput />
              <TextFieldInput color="primary" name="title" label="Title" rules={{ required: 'Title is required' }} />
              <TextFieldInput
                color="primary"
                multiline
                rows={5}
                name="description"
                label="Description"
                rules={{ required: 'Description is required' }}
              />
              <TextFieldInput color="primary" name="date" label="Date (format 'yyyy-MM-dd')" />
              <TextFieldInput color="primary" name="url" label="URL" />
              <HighlightInput />
              <FormControlLabel
                label="Use URL to upload cover image"
                control={
                  <Switch
                    value={urlEnabled}
                    onChange={e => {
                      setValue('cover_image', '');
                      setUrlEnabled(e.target.checked);
                    }}
                  />
                }
              />
              {urlEnabled ? (
                <TextFieldInput
                  color="primary"
                  name="cover_image"
                  label="Cover Image URL"
                  rules={{
                    required: 'Cover Image is required',
                    pattern: { value: /https?:\/\/.*/, message: 'Invalid URL' }
                  }}
                />
              ) : (
                <Dropzone
                  rules={{
                    accept: '.png, .jpg, .jpeg, .svg, .webp'
                  }}
                  onDropAccepted={files => {
                    setSelectedFile(files[0]);
                  }}
                  width="100%"
                  height={152}
                >
                  <Dropzone.Body>
                    <>
                      <Typography>
                        <Link component="span">Click to upload</Link> or drag and drop
                      </Typography>
                      <Typography color="text.secondary">
                        .png, .jpg, .jpeg, .svg, .webp files supported only (max. 3MB)
                      </Typography>
                    </>
                  </Dropzone.Body>

                  <Dropzone.Cover
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    bgcolor="background.paper"
                    in={!!selectedFile}
                  >
                    <DocumentScannerOutlined color="primary" />
                    <Typography>{selectedFile?.name}</Typography>
                  </Dropzone.Cover>
                </Dropzone>
              )}
              <ShowInAppFormGroup />
            </Box>
          )}
        </Edit>
      </form>
    </FormProvider>
  );
};

export default ThreatInformedDefenseEdit;
